import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, floor, sin, cos, mix, smoothstep, clamp, pow, max, abs, length, step,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Space-scape sky for the Boxing Ring map. The player's "on the sun" — the
// horizon ring glows molten orange matching the lava below, then ramps
// quickly into deep space with stars and nebulae filling the upper hemisphere.
//
// Vertex trick matches SkyMaterial: strip the camera translation (vec4(pos, 0)
// kills the translation column) then force clip-space z = w so after the
// perspective divide the depth is 1 — the sky renders behind everything else.
export class SpaceMaterial extends NodeMaterial {
  readonly uTime = uniform(0);

  constructor() {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;
    this.lights = false;

    this.vertexNode = Fn(() => {
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    // ------------------------------------------------------------------ utils

    const hash3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = vec3(
        p.dot(vec3(127.1, 311.7, 74.7)),
        p.dot(vec3(269.5, 183.3, 246.1)),
        p.dot(vec3(113.5, 271.9, 124.6))
      );
      return fract(sin(q).mul(43758.5453));
    });

    // Scalar 3D value noise — we use hash3(.).x as the cell hash so the
    // eight-corner lerp doesn't need a separate hash1 helper.
    const noise3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2))); // smoothstep weights

      const sample = (ox: number, oy: number, oz: number) =>
        hash3(i.add(vec3(ox, oy, oz))).x;

      const n000 = sample(0,0,0), n100 = sample(1,0,0);
      const n010 = sample(0,1,0), n110 = sample(1,1,0);
      const n001 = sample(0,0,1), n101 = sample(1,0,1);
      const n011 = sample(0,1,1), n111 = sample(1,1,1);

      const nx00 = mix(n000, n100, u.x);
      const nx10 = mix(n010, n110, u.x);
      const nx01 = mix(n001, n101, u.x);
      const nx11 = mix(n011, n111, u.x);
      const nxy0 = mix(nx00, nx10, u.y);
      const nxy1 = mix(nx01, nx11, u.y);
      return mix(nxy0, nxy1, u.z);
    });

    const fbm = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 5; i++) {
        sum.addAssign(noise3(q).mul(amp));
        q.assign(q.mul(2.03).add(vec3(13.1, 23.7, 7.2)));
        amp.assign(amp.mul(0.54));
      }
      return sum;
    });

    // Per-layer starfield — partition the view sphere into cells at `scale`,
    // keep only cells whose hash crosses `threshold`, draw a twinkling disk
    // at a hash-jittered position inside the kept cells.
    //
    // Size range is intentionally narrow (~1.5× smallest → largest) so the
    // whole population stays inside roughly a 2× spread when caller-side
    // sizeScale is factored in. The pow exponent (4.5) keeps the halo
    // tight so bloom catches a crisp core rather than a wide smear —
    // earlier versions with exponent 2.5 produced oversized "blob" stars
    // that dominated the sky visually.
    //
    // Two detuned twinkle sines with independent per-cell phase offsets
    // give each star its own flicker schedule instead of the whole sky
    // breathing in sync.
    const starLayer = Fn((args: [Node<'vec3'>, Node<'float'>, Node<'float'>, Node<'float'>]) => {
      const [d, scale, threshold, sizeScale] = args;
      const p = d.mul(scale);
      const cell = floor(p);
      const f = fract(p);
      const h = hash3(cell);

      // Star centre jittered inside the cell.
      const centre = vec3(0.5).add(h.sub(0.5).mul(0.7));
      const dist = length(f.sub(centre));
      const mask = step(threshold, h.x);

      const twinkleA = sin(this.uTime.mul(2.3).add(h.y.mul(25.0))).mul(0.28);
      const twinkleB = sin(this.uTime.mul(5.1).add(h.z.mul(41.0))).mul(0.17);
      const twinkle = twinkleA.add(twinkleB).add(0.62);
      // size ∈ [0.07, 0.11] in cell-units, scaled by caller.
      const size = h.z.mul(0.04).add(0.07).mul(sizeScale);
      const disk = pow(max(float(1).sub(dist.div(max(size, 0.0001))), 0), 4.5);

      return disk.mul(mask).mul(twinkle);
    });

    // ------------------------------------------------------------------- main

    this.colorNode = Fn(() => {
      const d = positionLocal.normalize().toVar();
      const h = d.y;

      // Base space gradient — near-black at zenith, faint purple-blue mid.
      // Below the horizon fades to pure black (we shouldn't actually see
      // below but keeping the gradient well-defined avoids surprises).
      const zenith = vec3(0.006, 0.004, 0.018);
      const midSky = vec3(0.030, 0.018, 0.070);
      const spaceBase = mix(midSky, zenith, smoothstep(0.2, 1.0, h));

      // -------- Horizon heat: "on the sun" ----------------------------------
      // A *narrow* band of molten orange: the hot crescent (~1°) plus a
      // ~3° haze halo above it. The rest of the sky is clear space. An
      // earlier version flooded the lower ~25° of the dome; shrunk ~8x
      // per user feedback so the cosmos dominates the view.
      const horizonHaze = smoothstep(0.055, -0.02, h);
      const heatCore = pow(max(float(1).sub(abs(h.sub(0.003)).mul(70)), 0), 2.0);
      const hotColor = vec3(3.6, 1.2, 0.28);    // bloom-pushing bright orange
      const hazeColor = vec3(0.9, 0.30, 0.08);  // ambient horizon warmth
      const heat = hazeColor.mul(horizonHaze.mul(0.55))
        .add(hotColor.mul(heatCore).mul(0.9));

      // Mask everything below the horizon — lava plane covers that half of
      // the sphere anyway, but the shader shouldn't be lighting it up either.
      const belowHorizon = smoothstep(-0.18, -0.02, h);

      // -------- Nebulae -----------------------------------------------------
      // Palette: red / purple / blue only. Cyan/pink blobs from the prior
      // version read as the dominant visual feature, competing with the
      // starfield; dropping them keeps this a backdrop. `nebC` selects
      // colour as a three-way gradient (red → purple → blue) so you get
      // both pure colours and the natural mixes between adjacent stops.
      //
      // Brightness: the pow(nebShape, 3.0) "bright core" term that pushed
      // nebula peaks past the bloom threshold has been removed. The soft
      // large-scale structure (the "hardest to see" layer flagged as
      // correct) lives entirely in the linear multiplier — the loud
      // mid-brightness blobs that were showing up are gone.
      const nebA = fbm(d.mul(1.9)).toVar();
      const nebB = fbm(d.mul(3.4).add(vec3(11.0, 5.0, 23.0))).toVar();
      const nebC = fbm(d.mul(1.3).add(vec3(47.0, 12.0, 91.0))).toVar();

      // Thresholds loosened vs the previous overly-strict pass so more
      // of the sky qualifies as nebula — user reported only catching one
      // blue patch across the whole dome.
      const nebShape = smoothstep(0.58, 0.85, nebA).mul(smoothstep(0.48, 0.78, nebB));
      // Per-colour balance. Previous pass overshot purple (too dominant)
      // and undershot red (invisible). Purple blue-channel halved; red
      // channel boosted further. Selector thresholds also shifted so red
      // gets a wider slice of the `nebC` distribution (most fbm values
      // cluster near 0.5, so the old 0.25–0.50 red band caught very few
      // pixels; 0.42–0.58 gives it roughly equal share to blue).
      const nebRed    = vec3(1.20, 0.20, 0.22);
      const nebPurple = vec3(0.55, 0.14, 0.90);
      const nebBlue   = vec3(0.12, 0.36, 1.10);
      const nebHue = mix(
        mix(nebRed, nebPurple, smoothstep(0.42, 0.58, nebC)),
        nebBlue,
        smoothstep(0.62, 0.80, nebC)
      );
      // Brightness ~6× the initial desaturation pass — user couldn't see
      // the nebulae at lower multipliers. Linear-only compose (no pow
      // core term) keeps the soft diffuse character even at high values;
      // peak red channel now crosses the bloom threshold (~0.85) so
      // brightest nebula regions get a gentle bloom lift, which helps
      // their visibility without the sharp "gas cloud blob" read.
      const nebBright = nebShape.mul(0.97);
      const nebula = nebHue.mul(nebBright).mul(smoothstep(0.06, 0.18, h));

      // -------- Stars -------------------------------------------------------
      // sizeScale values brought close together (was 1.3 / 0.9) so the
      // overall star population stays within a tight size band — combined
      // with the narrowed per-star size range in starLayer() this keeps
      // the largest stars at ~1.5–2× the smallest. Third distant-dust
      // layer added at 280 scale for the "faint pinpoints between the
      // named stars" feel the moon sky uses.
      // Density loosened another 5× on top of the prior pass — user still
      // wanted more stars. 0.970 → 0.85 (3% → 15%), 0.940 → 0.70 (6% →
      // 30%) gives a dense backdrop without becoming a solid glow.
      const stars1 = starLayer(d, 55,  0.85, 1.00);
      const stars2 = starLayer(d, 130, 0.70, 0.90);
      const starSum = stars1.mul(1.6).add(stars2.mul(1.0));
      const starCol = vec3(1.25, 1.15, 1.00).mul(starSum);
      // Fade stars out through the horizon haze region so they don't
      // punch through the heat band. With the new narrow heat band this
      // ramp starts almost immediately above the horizon line.
      const starFade = smoothstep(0.02, 0.08, h);
      const stars = starCol.mul(starFade);

      const skyAbove = spaceBase.add(nebula).add(stars);
      // Heat takes over near the horizon. Use the haze ramp as the mix factor
      // so nebulae/stars smoothly hand off to molten orange.
      const blended = mix(skyAbove, skyAbove.add(heat), clamp(horizonHaze, 0, 1))
        .add(hotColor.mul(heatCore).mul(0.9));

      const final = blended.mul(belowHorizon);
      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
